const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Product media gallery: scroll-snap slider + thumbnails, follows Horizon variant changes.
 * tk-pdp-sync is imported from the versioned URL in data-tk-sync-url.
 */
class TkPdpGallery extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #index = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.track?.addEventListener('keydown', this.#onKeydown, { signal });

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(Number(entry.target.getAttribute('data-index')));
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    for (const slide of this.slides) this.#observer.observe(slide);

    const initial = this.querySelector('[data-selected-media]')?.getAttribute('data-selected-media');
    if (initial) this.goToMedia(initial, false);

    if (this.hasAttribute('data-variant-scroll')) {
      import(this.dataset.tkSyncUrl ?? '')
        .then((sync) => {
          if (signal.aborted) return;
          sync.onVariantChange(
            this,
            (/** @type {any} */ { variant }) => {
              const mediaId = variant?.featured_media?.id;
              if (mediaId) this.goToMedia(String(mediaId));
            },
            signal
          );
        })
        .catch((error) => console.warn('[tk-pdp-gallery]', error));
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  get track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
  }

  get slides() {
    return /** @type {HTMLElement[]} */ ([...(this.track?.children ?? [])]);
  }

  /** True when the track scrolls horizontally (slider mode at the current breakpoint). */
  get isSlider() {
    const track = this.track;
    return Boolean(track && track.scrollWidth > track.clientWidth + 1);
  }

  /** @param {string} mediaId @param {boolean} [smooth] */
  goToMedia(mediaId, smooth = true) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0) this.goTo(index, smooth);
  }

  /** @param {number} index @param {boolean} [smooth] */
  goTo(index, smooth = true) {
    const slides = this.slides;
    const track = this.track;
    if (!track || slides.length === 0) return;
    const target = slides[(index + slides.length) % slides.length];
    if (!target) return;
    const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';

    if (this.isSlider) {
      // The track is position: relative, so offsetLeft is measured from the track itself.
      track.scrollTo({ left: target.offsetLeft, behavior });
    } else if (smooth) {
      const rect = target.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) target.scrollIntoView({ behavior, block: 'nearest' });
    }
    this.#setActive(Number(target.dataset.index));
  }

  /** @param {number} index */
  #setActive(index) {
    if (Number.isNaN(index)) return;
    this.#index = index;
    for (const thumb of this.querySelectorAll('[data-thumb]')) {
      const active = Number(thumb.getAttribute('data-thumb')) === index;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = String(index + 1);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !this.contains(button)) return;
    if (button.dataset.thumb !== undefined) this.goTo(Number(button.dataset.thumb));
    else if (button.dataset.dir === 'prev') this.goTo(this.#index - 1);
    else if (button.dataset.dir === 'next') this.goTo(this.#index + 1);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.target !== this.track) return;
    if (event.key === 'ArrowRight') this.goTo(this.#index + 1);
    else if (event.key === 'ArrowLeft') this.goTo(this.#index - 1);
    else return;
    event.preventDefault();
  };
}

if (!customElements.get('tk-pdp-gallery')) customElements.define('tk-pdp-gallery', TkPdpGallery);
